<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-card>
          <v-toolbar :dark="$vuetify.theme.dark" color="primary">
            <v-toolbar-title>
              {{ $t('Создание заявки на регистрацию торговой процедуры') }} ({{ $t(LOT_TYPE_ENUM[lotType]) }})
            </v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-row>
              <v-col>
                <request-result-info
                    :process-comment="request.processComment"
                    :request-status="request.status" />
              </v-col>
            </v-row>
            <custom-stepper
                v-model="stepNumber"
                :steps-param-array="stepsProperties"
                :loading="isLoading"
                :disabled-forward-btn="!isUserLoaded"
                :success="success">
              <template v-slot:main-1>
                <v-row>
                  <v-col>
                    <v-card>
                      <v-card-text>
                        <lot-info
                            v-if="!isAdmin"
                            ref="lotInfo"
                            :lot-info="lot"
                            :save-method="saveLotInfo"
                            :loading="isLoading"
                            :is-user-loaded="isUserLoaded"
                            :readonly="readonly" />
                        <admin-lot-info
                            v-else
                            ref="lotInfo"
                            :lot-info="lot"
                            :save-method="saveLotInfo"
                            :loading="isLoading"
                            :is-user-loaded="isUserLoaded"
                            :readonly="readonly" />
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </template>
              <template v-slot:main-2>
                <v-row>
                  <v-col>
                    <positions-list
                        v-if="lot.lotId"
                        :positions="positions"
                        :can-edit="lot.canEditPosition"
                        :can-add="lot.canCreatePosition"
                        :lot="lot"
                        :loading="isLoading"
                        @change="getPositionsList" />
                  </v-col>
                </v-row>
              </template>
              <template v-slot:main-3>
                <v-row>
                  <v-col>
                    <sessions-list
                        v-if="lot.lotId"
                        :sessions="sessions"
                        :lot="lot"
                        :positions-count="positionsCount"
                        :loading="isLoading"
                        @change="getSessionsList" />
                  </v-col>
                </v-row>
              </template>
              <template v-slot:main-4>
                <v-row>
                  <v-col>
                    <business-document-list
                        ref="requestFiles"
                        :request-files="requestFiles"
                        :document-types="requiredDocumentTypes"
                        :readonly="readonly"
                        :loading="isLoading"
                        :lot-request-info="lotRequestInfo"
                        @deleted="getLotRequestFiles"
                        @uploaded="attachFileToLotRequest" />
                  </v-col>
                </v-row>
              </template>
              <template v-slot:main-5>
                <v-row>
                  <v-col>
                    <lot-info-view :lot="lot" attachments positions sessions>
                      <template v-slot:attachments>
                        <business-document-list-view
                            :request-files="requestFiles"
                            :document-types="requiredDocumentTypes"
                            :loading="isLoading" />
                      </template>
                      <template v-slot:positions>
                        <positions-list
                            v-if="lot.lotId"
                            :lot="lot"
                            :loading="isLoading"
                            :positions="positions" />
                      </template>
                      <template v-slot:sessions>
                        <sessions-list
                            v-if="lot.lotId"
                            :sessions="sessions"
                            :lot="lot"
                            readonly
                            :positions-count="positionsCount"
                            :loading="isLoading" />
                      </template>
                      <template v-slot:footer>
                        <v-row v-if="validationMessages.length">
                          <v-col cols="12">
                            <v-alert
                                type="error"
                                outlined
                                :value="!!validationMessages.length">
                              <ul>
                                <li
                                    v-for="(message, index) in validationMessages"
                                    :key="index">
                                  {{ message }}
                                </li>
                              </ul>
                            </v-alert>
                          </v-col>
                        </v-row>
                      </template>
                    </lot-info-view>
                  </v-col>

                </v-row>
              </template>
              <template v-slot:buttons-5>
                <v-btn
                    v-if="canSendRequest"
                    id="sendBtn"
                    color="primary"
                    :disabled="!!validationMessages.length || isLoading"
                    @click="createLotRequest">
                  {{ $t('Отправить') }}
                </v-btn>
              </template>
            </custom-stepper>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import Vue from 'vue'
  import CustomStepper from '@/components/reused/ui/CustomStepper.vue'
  import LotInfo from '@/components/reused/lot/LotInfo.vue'
  import AdminLotInfo from '@/components/reused/lot/AdminLotInfo.vue'
  import LotInfoView from '@/components/reused/lot/LotInfoView.vue'
  import BusinessDocumentList from '@/components/reused/files/BusinessDocumentList.vue'
  import BusinessDocumentListView from '@/components/reused/files/BusinessDocumentListView.vue'
  import SessionsList from '@/components/reused/sessions/SessionsList.vue'
  import PositionsList from '@/components/reused/positions/PositionsList.vue'
  import RequestResultInfo from './RequestResultInfo.vue'
  import { LOT_TYPE_ENUM } from '@/constants/enums'
  import {
    LOT_REQUEST_TYPES,
    LOT_REQUEST_STATUSES,
    LOT_STATUSES
  } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import {
    LOT_MANAGEMENT,
    LOT_POSITIONS_MANAGEMENT,
    LOT_SESSIONS_MANAGEMENT,
    LOT_REQUESTS_MANAGEMENT,
    BUSINESS_FILES,
    CURRENT_USER,
    CURRENT_ORGANIZATION
  } from '@/constants/store.js'

  export default {
    components: {
      CustomStepper,
      LotInfo,
      BusinessDocumentList,
      BusinessDocumentListView,
      LotInfoView,
      RequestResultInfo,
      SessionsList,
      PositionsList,
      AdminLotInfo
    },
    props: {
      lotId: {
        type: String
      },
      lotType: {
        type: String,
        required: true
      }
    },
    data () {
      return {
        LOT_TYPE_ENUM,
        requestType: LOT_REQUEST_TYPES.REGISTRATION,
        stepNumber: 1,
        isLoading: false,
        success: false
      }
    },
    computed: {
      ...mapState(LOT_MANAGEMENT.NAMESPACE, {
        currentLot: state => (state[LOT_MANAGEMENT.STATE.CURRENT_LOT])
      }),
      ...mapState(LOT_POSITIONS_MANAGEMENT.NAMESPACE, {
        positions: state => (state[LOT_POSITIONS_MANAGEMENT.STATE.POSITIONS] || [])
      }),
      ...mapState(LOT_SESSIONS_MANAGEMENT.NAMESPACE, {
        sessions: state => (state[LOT_SESSIONS_MANAGEMENT.STATE.SESSIONS] || [])
      }),
      ...mapState(LOT_REQUESTS_MANAGEMENT.NAMESPACE, {
        request: state => (state[LOT_REQUESTS_MANAGEMENT.STATE.LAST] || {})
      }),
      ...mapState(BUSINESS_FILES.NAMESPACE, {
        requestFiles: state => (state[BUSINESS_FILES.STATE.REQUEST_FILES] || []),
        requiredDocumentTypes: state => (state[BUSINESS_FILES.STATE.REQUIRED_DOCUMENT_TYPES] || [])
      }),
      ...mapState(CURRENT_USER.NAMESPACE, {
        currentUser: state => state
      }),
      isUserLoaded () {
        return !!this.currentUser.contactPerson
      },
      lotRequestInfo () {
        return {
          lotId: this.lot.lotId,
          lotType: this.lot.lotType,
          requestType: this.requestType,
          creatorId: this.lot.creatorId
        }
      },
      stepsProperties () {
        return [
          {
            stepNumber: 1,
            stepTitle: this.$t('Информация по торговой процедуре'),
            stepTip: this.$t('Основная информация по торговой процедуре'),
            successText: this.$t('Изменения сохранены'),
            toNextStep: this.validateAndSaveLotInfo,
            toPrevStep: null,
            cancel: this.exitFromWizard
          },
          {
            stepNumber: 2,
            stepTitle: this.$t('Добавление позиций по торговой процедуре'),
            stepTip: '',
            successText: '',
            toNextStep: this.validatePositions,
            toPrevStep: this.toPrevStep,
            cancel: this.exitFromWizard
          },
          {
            stepNumber: 3,
            stepTitle: this.$t('Создание сессии'),
            stepTip: '',
            successText: '',
            toNextStep: this.validateSessions,
            toPrevStep: this.toPrevStep,
            cancel: this.exitFromWizard
          },
          {
            stepNumber: 4,
            stepTitle: this.$t('Добавление документов'),
            stepTip: '',
            successText: '',
            toNextStep: this.validateRequestFiles,
            toPrevStep: this.toPrevStep,
            cancel: this.exitFromWizard
          },
          {
            stepNumber: 5,
            stepTitle: this.$t('Регистрация торговой процедуры'),
            stepTip: '',
            successText: this.$t('Запрос успешно отправлен на обработку.'),
            toNextStep: null,
            toPrevStep: this.toPrevStep,
            cancel: this.exitFromWizard
          }
        ]
      },
      readonly () {
        return !!this.lot.lotId && !this.canSendRequest
      },
      canSendRequest () {
        const status = this.request.status

        return !status ||
          status === LOT_REQUEST_STATUSES.REJECTED
      },
      lot () {
        return this.currentLot || {
          lotId: this.lotId,
          lotType: this.lotType
        }
      },
      validationMessages () {
        return []
      },
      positionsCount () {
        return this.positions.length
      }
    },
    watch: {
      lotId (newVal, oldVal) {
        const vm = this
        if (!newVal && oldVal) {
          vm.clearStore().then(() => {
            vm.stepNumber = 1
          })
        }
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        if (vm.lotId && vm.lotType) {
          vm.initialize()
        }
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(LOT_MANAGEMENT.NAMESPACE, {
        getLotInfoAction: LOT_MANAGEMENT.ACTIONS.GET_LOT_INFO,
        updateLotInfoAction: LOT_MANAGEMENT.ACTIONS.UPDATE_LOT_INFO,
        createLotInfoAction: LOT_MANAGEMENT.ACTIONS.CREATE_LOT_INFO,
        clearLotInfoAction: LOT_MANAGEMENT.ACTIONS.CLEAR_LOT_INFO
      }),
      ...mapActions(LOT_REQUESTS_MANAGEMENT.NAMESPACE, {
        getLastLotRequestInfoAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.GET_LAST_LOT_REQUEST_INFO,
        createLotRequestAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.CREATE_LOT_REQUEST,
        clearLastLotRequestInfoAction: LOT_REQUESTS_MANAGEMENT.ACTIONS.CLEAR_LAST_LOT_REQUEST_INFO
      }),
      ...mapActions(LOT_POSITIONS_MANAGEMENT.NAMESPACE, {
        getPositionsListAction: LOT_POSITIONS_MANAGEMENT.ACTIONS.GET_POSITIONS_LIST,
        clearPositionsListAction: LOT_POSITIONS_MANAGEMENT.ACTIONS.CLEAR_POSITIONS_LIST
      }),
      ...mapActions(LOT_SESSIONS_MANAGEMENT.NAMESPACE, {
        getSessionsListAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.GET_SESSIONS_LIST,
        clearSessionsListAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.CLEAR_SESSIONS_LIST
      }),
      ...mapActions(BUSINESS_FILES.NAMESPACE, {
        getLotRequestFilesAction: BUSINESS_FILES.ACTIONS.GET_LOT_REQUEST_FILES,
        getLotRequestRequiredDocumentTypesAction: BUSINESS_FILES.ACTIONS.GET_LOT_REQUEST_REQUIRED_DOCUMENT_TYPES,
        attachFileToLotRequestAction: BUSINESS_FILES.ACTIONS.ATTACH_FILE_TO_LOT_REQUEST,
        clearLotRequestFilesAction: BUSINESS_FILES.ACTIONS.CLEAR_LOT_REQUEST_FILES,
        clearLotRequestRequiredDocumentTypesAction: BUSINESS_FILES.ACTIONS.CLEAR_LOT_REQUEST_REQUIRED_DOCUMENT_TYPES
      }),
      ...mapActions(CURRENT_ORGANIZATION.NAMESPACE, {
        getCurrentOrganizationAction: CURRENT_ORGANIZATION.ACTIONS.GET_CURRENT_ORGANIZATION,
        clearCurrentOrganizationAction: CURRENT_ORGANIZATION.ACTIONS.CLEAR_CURRENT_ORGANIZATION
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearLotInfoAction(),
            this.clearLastLotRequestInfoAction(),
            this.clearLotRequestRequiredDocumentTypesAction(),
            this.clearLotRequestFilesAction(),
            this.clearPositionsListAction(),
            this.clearSessionsListAction(),
            this.clearCurrentOrganizationAction()
          ])
      },
      initialize: async function () {
        this.isLoading = true
        const model = {
          requestType: this.requestType,
          lotType: this.lot.lotType,
          lotId: this.lot.lotId
        }
        try {
          await Promise.all([
            this.getLotInfoAction(model),
            this.getLastLotRequestInfoAction(model),
            this.getLotRequestRequiredDocumentTypesAction(model),
            this.getLotRequestFiles(),
            this.getPositionsListAction(model),
            this.getSessionsListAction(model),
            this.getCurrentOrganizationAction()
          ])
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      attachFileToLotRequest: async function (file) {
        this.isLoading = true
        const model = {
          requestType: this.requestType,
          lotType: this.lot.lotType,
          lotId: this.lot.lotId
        }
        try {
          await this.attachFileToLotRequestAction({
            ...model,
            fileId: file.fileId
          })
          await this.getLotRequestFiles()
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      getLotRequestFiles: async function () {
        this.isLoading = true
        const model = {
          requestType: this.requestType,
          lotId: this.lot.lotId,
          lotType: this.lot.lotType
        }
        try {
          await this.getLotRequestFilesAction(model)
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      validateAndSaveLotInfo: async function (step) {
        try {
          if (this.readonly) {
            this.toNextStep(step)
            return
          }
          await this.$refs.lotInfo.validateAndSave()
          // после изменений получаем полную информацию по торговой процедуре
          if (!this.lotId) {
            await this.replaceCurrentRoute(`${this.urlRootPath}/lotWizard/${this.lot.lotType}/${this.lot.lotId}`)
            // инициализируем список документов
            this.$nextTick(async () => {
              await this.initialize()
              this.toNextStep(step)
            })
          } else {
            await this.getLotInfoAction({
              lotId: this.lot.lotId,
              lotType: this.lot.lotType
            })
            this.toNextStep(step)
          }
        } catch (error) {
          this.throwIfUnknownError(error)
          // если ошибка, дальше не переходим
        }
      },
      validateRequestFiles: async function (step) {
        try {
          if (this.readonly) {
            this.toNextStep(step)
            return
          }
          // проверяем данные документов
          await this.$refs.requestFiles.validate()
          //  на следующий шаг
          this.toNextStep(step)
        } catch (error) {
          this.throwIfNotValidationError(error)
          // если ошибка, дальше не переходим
        }
      },
      validatePositions: async function (step) {
        try {
          if (this.readonly) {
            this.toNextStep(step)
            return
          }
          // проверяем наличие позиций
          if (!this.positions.length) {
            throw new Vue.errorsTypes.ValidationError(this.$t('Для продолжения необходимо добавить позицию.'))
          }
          //  на следующий шаг
          this.toNextStep(step)
        } catch (error) {
          this.throwIfNotValidationError(error)
          // если ошибка, дальше не переходим
        }
      },
      validateSessions: async function (step) {
        try {
          if (this.readonly) {
            this.toNextStep(step)
            return
          }
          // проверяем наличие сессий
          if (!this.sessions.length) {
            throw new Vue.errorsTypes.ValidationError(this.$t('Для продолжения необходимо добавить сессию.'))
          }
          //  на следующий шаг
          this.toNextStep(step)
        } catch (error) {
          this.throwIfNotValidationError(error)
          // если ошибка, дальше не переходим
        }
      },
      saveLotInfo: async function (lot) {
        this.isLoading = true
        this.success = false
        try {
          const model = {
            lot,
            lotType: this.lot.lotType,
            lotId: this.lot.lotId
          }

          if (this.lot.lotId) {
            await this.updateLotInfoAction(model)
          } else {
            await this.createLotInfoAction(model)
          }

          this.success = true
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          // для запрета перехода на следующий шаг пробрасываем ошибку
          throw error
        }
      },
      validateBeforeRequest: function () {
        const requestFiles = this.requestFiles || []
        if (!requestFiles.length) {
          throw new Vue.errorsTypes.ValidationError(this.$t('Нет прикрепленных файлов для отправки запроса.'))
        }
        if (this.validationMessages.length) {
          throw new Vue.errorsTypes.ValidationError()
        }
      },
      createLotRequest: async function () {
        try {
          this.validateBeforeRequest()
          if (!this.requestFiles.length) {
            throw new Vue.errorsTypes.ValidationError(this.$t('Документы по торговой процедуре не добавлены.'))
          }
          this.isLoading = true
          this.success = false
          await this.createLotRequestAction({
            request: this.requestFiles,
            lotId: this.lot.lotId,
            requestType: this.requestType,
            lotType: this.lot.lotType
          })
          await Promise.all([
            this.getLastLotRequestInfoAction({
              lotId: this.lot.lotId,
              requestType: this.requestType,
              lotType: this.lot.lotType
            }),
            this.getLotInfoAction({
              lotId: this.lot.lotId,
              lotType: this.lot.lotType
            }),
            this.getLotRequestFiles()
          ])
          this.isLoading = false
          this.success = true
        } catch (error) {
          this.isLoading = false
          this.throwIfUnknownError(error)
          // для запрета перехода на следующий шаг пробрасываем ошибку
        }
      },
      getPositionsList: async function () {
        try {
          this.isLoading = true
          const model = {
            lotId: this.lot.lotId,
            lotType: this.lot.lotType
          }
          await Promise.all([
            // для получения флагов доступа
            this.getLotInfoAction(model),
            // для обновления списка
            this.getPositionsListAction(model)]
          )
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      getSessionsList: async function () {
        try {
          this.isLoading = true
          const model = {
            lotId: this.lot.lotId,
            lotType: this.lot.lotType
          }
          await Promise.all([
            // для получения флагов доступа
            this.getLotInfoAction(model),
            // для обновления списка
            this.getSessionsListAction(model),
            this.getPositionsListAction(model)
          ])
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      toNextStep: async function (step) {
        this.success = false
        this.stepNumber = step + 1
      },
      toPrevStep: function (step) {
        this.success = false
        this.stepNumber = step - 1
      },
      exitFromWizard: function () {
        this.goToRoute(`${this.urlRootPath}/lots/personal/${LOT_STATUSES.REGISTERED}`)
      }
    }
  }
</script>
